import { createRootRoute, createRoute, createRouter, redirect } from '@tanstack/react-router';
import { App } from './App';
import { ShellLayout } from './components/app-shell';
import { RequireAuth } from './components/require-auth';
import { ActivityPage } from './routes/activity-page';
import { legacyBatchRedirect } from './routes/batch-lib';
import { BatchesIndexView, BatchesPage, BatchWorkView } from './routes/batches-page';
import { CapsPage } from './routes/caps-page';
import { CatalogPage } from './routes/catalog-page';
import { CountShelfPage } from './routes/count-shelf-page';
import { DesignsPage } from './routes/designs-page';
import { FulfillPage } from './routes/fulfill-page';
import { GarmentStockPage } from './routes/garment-stock-page';
import { InventoryHubLayout } from './routes/inventory-hub';
import { InventoryInboundPage } from './routes/inventory-inbound-page';
import { InventoryPage } from './routes/inventory-page';
import { LoginPage } from './routes/login';
import { OrderDetailPage } from './routes/order-detail-page';
import { OrdersPage } from './routes/orders-page';
import { PressStationPage } from './routes/press-station-page';
import { PressesPage } from './routes/presses-page';
import { PricingConfigPage } from './routes/pricing-config-page';
import { ProductionBoardPage } from './routes/production-board-page';
import { PurchaseDraftPage } from './routes/purchase-draft-page';
import { ReceivingPage } from './routes/receiving-page';
import { ReprintQueuePage } from './routes/reprint-queue-page';
import { ResetPasswordPage } from './routes/reset-password';
import { RollsPage } from './routes/rolls-page';
import { ScanStationPage } from './routes/scan-station-page';
import { ScreenPrintPage } from './routes/screen-print-page';
import { ScreenPrintQuotePage } from './routes/screen-print-quote';
import { SettingsPurchasingPage } from './routes/settings-purchasing-page';
import { ShipStationPage } from './routes/ship-station-page';
import { StockItemsPage } from './routes/stock-items-page';
import { StoresPage } from './routes/stores-page';
import { TransferInventoryPage } from './routes/transfer-inventory-page';

const rootRoute = createRootRoute({ component: App });

// ── The app shell is a PATHLESS LAYOUT ROUTE ─────────────────────────────────────────────────────
// `ShellLayout` (sidebar + header + <Outlet />) mounts ONCE and stays mounted while the user moves
// between the routes nested under it, so `SidebarProvider` state — the collapsed rail, the open phone
// sheet and, next, the open nav groups — survives navigation. Before this every route wrapped its own
// `<AppShell>`; a route change swapped component identity, React remounted the shell, and the sidebar
// snapped back to `defaultOpen` on every click (sidebar-nav audit F6).
//
// A pathless route contributes nothing to the URL — the children keep the full paths they always had.
// What DOES change is their route ID: `/_shell/orders/$storeId/$orderId`, not
// `/orders/$storeId/$orderId`. `useParams({ from })` keys on the ID (order-detail-page.tsx), whereas
// `Link to=`, `redirect({ to })` and `navigate({ to })` take paths and are unaffected.
//
// Deliberately NOT under the shell: `/login`, `/reset-password`, the two full-bleed kiosks (`/scan`,
// `/ship`) and the pure redirects.
const shellRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: '_shell',
  component: ShellLayout,
});

// Default landing → the Orders queue (the daily driver), not the screen-print quote form. The quote
// form keeps its own path below. Redirect at the router level so `/orders` is the one canonical URL.
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/orders' });
  },
});

const screenPrintQuoteRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/quote/screen-print',
  component: ScreenPrintQuotePage,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
});

const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reset-password',
  component: ResetPasswordPage,
});

// ── Settings live under /settings/* (sidebar PR-2, D7) ───────────────────────────────────────────
// The three configuration pages moved out of the top level; their old URLs redirect below.
const pricingConfigRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/settings/pricing',
  component: PricingConfigPage,
});

const ordersRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/orders',
  component: OrdersPage,
});

// The order "job card" — reached by clicking an order in the queue or the WIP board (not top-level
// nav). App's first param route; `useParams({ from: '/_shell/orders/$storeId/$orderId' })` reads the
// params — by route ID, which includes the pathless shell above.
const orderDetailRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/orders/$storeId/$orderId',
  component: OrderDetailPage,
});

const designsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/designs',
  component: DesignsPage,
});

const catalogRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/catalog',
  component: CatalogPage,
});

const storesRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/settings/stores',
  component: StoresPage,
});

// Purchasing configuration — the reorder model, the ship-to address and the S&S payment method — given
// a page of its own (sidebar PR-3); the dialogs it opens are the ones the Inventory pages already use.
const settingsPurchasingRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/settings/purchasing',
  component: SettingsPurchasingPage,
});

const reprintQueueRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/reprint-queue',
  component: ReprintQueuePage,
});

// The gang-sheet WORKBENCH (gang-sheets redesign v1, PR-1): `BatchesPage` is the rail + an Outlet; the
// index child renders the default sheet's card and `$batchId` the one the URL names, so the selection
// lives in the URL (refresh, back, share). The legacy `?batch=<id>` deep link — still sent by the order
// card and the production board — redirects to the child route rather than being read by the page.
const batchesRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/batches',
  validateSearch: (search: Record<string, unknown>): { batch?: string } => {
    const batch = legacyBatchRedirect(search);
    return batch ? { batch } : {};
  },
  beforeLoad: ({ search }) => {
    if (search.batch) {
      throw redirect({
        to: '/batches/$batchId',
        params: { batchId: search.batch },
        replace: true,
      });
    }
  },
  component: BatchesPage,
});

const batchesIndexRoute = createRoute({
  getParentRoute: () => batchesRoute,
  path: '/',
  component: BatchesIndexView,
});

const batchDetailRoute = createRoute({
  getParentRoute: () => batchesRoute,
  path: '$batchId',
  component: BatchWorkView,
});

const activityRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/activity',
  component: ActivityPage,
});

const pressStationRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/press',
  component: PressStationPage,
});

// Full-bleed kiosk: `/scan` renders WITHOUT the shell (no sidebar/header) — just the auth gate. The
// station owns its top bar + full-screen forced-dark layout (touch-first for the shop-floor iPad).
// RequireAuth is the same gate the shell uses internally, so the shared station account still applies.
const scanRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/scan',
  component: () => (
    <RequireAuth>
      <ScanStationPage />
    </RequireAuth>
  ),
});

// Full-bleed kiosk like `/scan`: the shipping bench is its own device (its default printer IS the
// 4x6 Rollo), so the station owns the whole screen — no sidebar to mis-tap while packing a box.
const shipStationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/ship',
  component: () => (
    <RequireAuth>
      <ShipStationPage />
    </RequireAuth>
  ),
});

const capsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/caps',
  component: CapsPage,
});

const screenPrintRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/screen-print',
  component: ScreenPrintPage,
});

const fulfillRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/fulfill',
  component: FulfillPage,
});

const productionRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/production',
  component: ProductionBoardPage,
});

const rollsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/settings/film-rolls',
  component: RollsPage,
});

// The shop's screen-printing presses — stations · heads · flash stations (screen-print station P2).
const pressesRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/settings/presses',
  component: PressesPage,
});

// The blank-inventory hub — one sidebar item; the five former pages are tabs under this layout
// (routes/inventory-hub.tsx). The shell wraps the layout ONCE; children render bare into its Outlet.
const inventoryHubRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/inventory',
  component: InventoryHubLayout,
});

const inventoryOnHandRoute = createRoute({
  getParentRoute: () => inventoryHubRoute,
  path: '/',
  component: InventoryPage,
});

// "Short & reorder" merged into the On-hand tab as a filter chip (UI refresh 2026-08); the route
// redirects so bookmarks + muscle memory land on the same rows. The two older redirects (demand /
// reorder → short, from the Phase-1a merge) chain onto the same target.
const inventoryShortRedirect = createRoute({
  getParentRoute: () => inventoryHubRoute,
  path: 'short',
  beforeLoad: () => {
    throw redirect({ to: '/inventory', search: { filter: 'short' } });
  },
});

const inventoryDemandRedirect = createRoute({
  getParentRoute: () => inventoryHubRoute,
  path: 'demand',
  beforeLoad: () => {
    throw redirect({ to: '/inventory', search: { filter: 'short' } });
  },
});

const inventoryReorderRedirect = createRoute({
  getParentRoute: () => inventoryHubRoute,
  path: 'reorder',
  beforeLoad: () => {
    throw redirect({ to: '/inventory', search: { filter: 'below' } });
  },
});

const purchaseDraftRoute = createRoute({
  getParentRoute: () => inventoryHubRoute,
  path: 'purchase-draft',
  component: PurchaseDraftPage,
});

const inventoryInboundRoute = createRoute({
  getParentRoute: () => inventoryHubRoute,
  path: 'inbound',
  component: InventoryInboundPage,
});

const receivingRoute = createRoute({
  getParentRoute: () => inventoryHubRoute,
  path: 'receiving',
  component: ReceivingPage,
});

// Printed-transfer (film) inventory — physical printed DTF transfers on the shelf (Phase 0).
const inventoryTransfersRoute = createRoute({
  getParentRoute: () => inventoryHubRoute,
  path: 'transfers',
  component: TransferInventoryPage,
});

// Finished-garment shelf — pressed, ready-to-ship shirts (finished-garment-stock v1, PR-2).
const inventoryGarmentsRoute = createRoute({
  getParentRoute: () => inventoryHubRoute,
  path: 'garments',
  component: GarmentStockPage,
});

// Internal stock-item catalog — Ship-only shelf goods (inventory-clarity P2b, Phase A).
const inventoryStockItemsRoute = createRoute({
  getParentRoute: () => inventoryHubRoute,
  path: 'items',
  component: StockItemsPage,
});

// Old top-level /receiving → /inventory/receiving (preserve bookmarks / operator muscle memory).
const receivingRedirectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/receiving',
  beforeLoad: () => {
    throw redirect({ to: '/inventory/receiving' });
  },
});

// The old top-level settings URLs → their /settings/* homes, so bookmarks and muscle memory keep
// working (the /receiving precedent above). `/stores` ALSO carries its search params: the Shopify OAuth
// callback (rest-api routes/shopify-oauth.ts) still sends the browser back to `/stores?connected=…` /
// `?install=…`, and the stores page reads those flags after mount — drop them here and a successful
// connect would land on a silent page.
const storesRedirectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/stores',
  beforeLoad: () => {
    throw redirect({ to: '/settings/stores', search: true });
  },
});

const rollsRedirectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/rolls',
  beforeLoad: () => {
    throw redirect({ to: '/settings/film-rolls' });
  },
});

const pricingConfigRedirectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/pricing-config',
  beforeLoad: () => {
    throw redirect({ to: '/settings/pricing' });
  },
});

// `/settings` has no page of its own yet (D8): land on the first row.
const settingsIndexRedirectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings',
  beforeLoad: () => {
    throw redirect({ to: '/settings/stores' });
  },
});

// The shelf count is a TASK, not an inventory view, so it sits at the top level next to /press rather
// than under the /inventory hub — a hub child would inherit the KPI strip and the seven-tab bar, which
// is a lot of a phone screen to spend before the first bin. It keeps the shell (unlike the /scan kiosk)
// because the desktop half of this screen wants the sidebar to navigate away afterwards.
const countShelfRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/count',
  // `?focus=<ssSku>` — the hub's "count it" items land on one bin (pulled-blanks v2 B2).
  validateSearch: (search: Record<string, unknown>): { focus?: string } =>
    typeof search.focus === 'string' && search.focus.length > 0 ? { focus: search.focus } : {},
  component: CountShelfPage,
});

// Exported (not just `router`) so a test can build the same tree on a memory history and prove the
// shell persists across a navigation — see test/unit/router-shell.test.tsx.
export const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  resetPasswordRoute,
  scanRoute,
  shipStationRoute,
  receivingRedirectRoute,
  storesRedirectRoute,
  rollsRedirectRoute,
  pricingConfigRedirectRoute,
  settingsIndexRedirectRoute,
  shellRoute.addChildren([
    screenPrintQuoteRoute,
    pricingConfigRoute,
    ordersRoute,
    orderDetailRoute,
    designsRoute,
    catalogRoute,
    storesRoute,
    settingsPurchasingRoute,
    batchesRoute.addChildren([batchesIndexRoute, batchDetailRoute]),
    reprintQueueRoute,
    pressStationRoute,
    capsRoute,
    screenPrintRoute,
    fulfillRoute,
    productionRoute,
    activityRoute,
    rollsRoute,
    pressesRoute,
    inventoryHubRoute.addChildren([
      inventoryOnHandRoute,
      inventoryShortRedirect,
      inventoryDemandRedirect,
      inventoryReorderRedirect,
      purchaseDraftRoute,
      inventoryInboundRoute,
      receivingRoute,
      inventoryTransfersRoute,
      inventoryGarmentsRoute,
      inventoryStockItemsRoute,
    ]),
    countShelfRoute,
  ]),
]);

export const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
